import { useCallback } from 'react';

export function useLocalStorage() {
  type CacheData<T> = {
    data: T;
    timestamp: number;
    userId?: string;
  };

  const getCurrentUserId = useCallback((): string => {
    //uses user id hidden field from layout/index.master
    const userIdElement = document.getElementById('UserId') as HTMLInputElement;
    return userIdElement?.value || 'unknown';
  }, []);

  const updateCacheItem = useCallback(<T>(key: string, data: T, expiryMinutes?: number): void => {
    const currentUserId = getCurrentUserId();
    const userSpecificKey = `${key}_${currentUserId}`;
    
    const cacheData: CacheData<T> = {
      data,
      timestamp: Date.now(),
      userId: currentUserId
    };
    
    if (expiryMinutes) {
      (cacheData as any).expiryMinutes = expiryMinutes;
    }
    
    localStorage.setItem(userSpecificKey, JSON.stringify(cacheData));
  }, [getCurrentUserId]);

  const getCacheItem = useCallback(<T>(key: string): T | null => {
    const currentUserId = getCurrentUserId();
    const userSpecificKey = `${key}_${currentUserId}`;
    const cached = localStorage.getItem(userSpecificKey);
    
    if (!cached) return null;

    const { data, timestamp, userId, expiryMinutes }: CacheData<T> & { expiryMinutes?: number } = JSON.parse(cached);
    
    if (userId && userId !== currentUserId) {
      localStorage.removeItem(userSpecificKey);
      return null;
    }

    if (expiryMinutes) {
      const now = Date.now();
      const expiryTime = timestamp + (expiryMinutes * 60 * 1000);
      
      if (now >= expiryTime) {
        localStorage.removeItem(userSpecificKey);
        return null;
      }
    }

    return data;
  }, [getCurrentUserId]);

  const removeCacheItem = useCallback((key: string): void => {
    const currentUserId = getCurrentUserId();
    const userSpecificKey = `${key}_${currentUserId}`;
    localStorage.removeItem(userSpecificKey);
  }, [getCurrentUserId]);

  const clearUserCache = useCallback((userId?: string): void => {
    const targetUserId = userId || getCurrentUserId();
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.endsWith(`_${targetUserId}`)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, [getCurrentUserId]);

  const clearAllCache = useCallback((): void => {
    localStorage.clear();
  }, []);

  return { 
    updateCacheItem, 
    getCacheItem, 
    removeCacheItem, 
    clearUserCache, 
    clearAllCache,
    getCurrentUserId 
  };
}
